/**
 * Клиент сетевой части: каталог игр, комнаты, сессии (docs/ROOMS.md).
 *
 * Куки участника и администратора ставит сервер, поэтому все запросы идут
 * с credentials: 'include' — иначе браузер не отправит их обратно.
 */
import { ApiError } from './errors'

export type GameMode = 'mute' | 'replace' | 'overlay'

export type Game = {
  id: string
  title: string
  description: string
  sourceProjectId: string
  videoFile: string
  duration: number
  mode: GameMode
  published: boolean
  rolesCount: number
  linesCount: number
  createdAt: string
  updatedAt: string
}

export type GameRole = { id: string; gameId: string; name: string; color: string }

export type GameLine = {
  id: string
  gameId: string
  roleId: string
  start: number
  end: number
  text: string
  textOriginal: string
}

export type GameTrack = {
  id: string
  gameId: string
  kind: string
  file: string
  duration: number
  label: string
}

export type GameFull = {
  game: Game
  roles: GameRole[]
  lines: GameLine[]
  tracks: GameTrack[]
}

export type RoomState = 'lobby' | 'playing' | 'result'

export type Room = {
  id: string
  code: string
  title: string
  state: RoomState
  gameId: string
  sessionId: string
  createdAt: string
}

export type Participant = {
  id: string
  roomId: string
  name: string
  isAdmin: boolean
  online: boolean
  joinedAt: string
}

export type Assignment = { roleId: string; participantId: string }

export type SessionState = 'recording' | 'rendering' | 'done' | 'cancelled'

export type Session = {
  id: string
  roomId: string
  gameId: string
  state: SessionState
  renderFile: string
  error: string
  assignments: Assignment[]
  createdAt: string
}

export type RoomView = {
  room: Room
  participants: Participant[]
  sessions: Session[]
  game: GameFull | null
  link: string
  isAdmin: boolean
  me: Participant | null
}

export type ParticipantStat = {
  participantId: string
  name: string
  recorded: number
  total: number
}

export type SessionView = {
  session: Session
  game: GameFull
  progress: ParticipantStat[]
  myLines: string[]
  recorded: string[]
  participants: Participant[]
}

export type AuthState = { isAdmin: boolean; authRequired: boolean }

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init })
  const text = await res.text()
  if (!res.ok) throw ApiError.fromBody(res.status, text)
  return (text ? JSON.parse(text) : null) as T
}

function json(body: unknown): RequestInit {
  return { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
}

// ── авторизация ───────────────────────────────────────────────────────────

export const auth = {
  me: () => request<AuthState>('/api/auth/me'),
  login: (password: string) => request<AuthState>('/api/auth/login', json({ password })),
  logout: () => request<null>('/api/auth/logout', { method: 'POST' }),
}

// ── игры ──────────────────────────────────────────────────────────────────

export const games = {
  list: () => request<Game[]>('/api/games'),
  get: (id: string) => request<GameFull>(`/api/games/${id}`),
  publish: (projectId: string, body: { title?: string; description?: string }) =>
    request<Game>(`/api/projects/${projectId}/publish`, json(body)),
  patch: (id: string, body: { title?: string; description?: string; published?: boolean }) =>
    request<Game>(`/api/games/${id}`, { ...json(body), method: 'PATCH' }),
  remove: (id: string) => request<null>(`/api/games/${id}`, { method: 'DELETE' }),
  videoUrl: (id: string) => `/media/games/${id}/video`,
  trackUrl: (gameId: string, trackId: string) => `/media/games/${gameId}/tracks/${trackId}`,
}

// ── комнаты ───────────────────────────────────────────────────────────────

export const rooms = {
  create: (title: string) => request<RoomView>('/api/rooms', json({ title })),
  list: () => request<Room[]>('/api/rooms'),
  get: (roomOrCode: string) => request<RoomView>(`/api/rooms/${roomOrCode}`),
  join: (code: string, name: string) => request<RoomView>(`/api/rooms/${code}/join`, json({ name })),
  setGame: (room: string, gameId: string) =>
    request<RoomView>(`/api/rooms/${room}`, { ...json({ gameId }), method: 'PATCH' }),
  remove: (room: string) => request<null>(`/api/rooms/${room}`, { method: 'DELETE' }),
  kick: (room: string, participantId: string) =>
    request<null>(`/api/rooms/${room}/participants/${participantId}`, { method: 'DELETE' }),
  start: (room: string, gameId: string, assignments: Assignment[]) =>
    request<SessionView>(`/api/rooms/${room}/session`, json({ gameId, assignments })),
  back: (room: string) => request<RoomView>(`/api/rooms/${room}/back`, json({})),
}

// ── сессии ────────────────────────────────────────────────────────────────

export const sessions = {
  get: (id: string) => request<SessionView>(`/api/sessions/${id}`),
  finish: (id: string) => request<{ state: string }>(`/api/sessions/${id}/finish`, json({})),
  resultUrl: (id: string) => `/media/sessions/${id}/result`,
  async upload(
    sessionId: string,
    lineId: string,
    blob: Blob,
    fields: { duration: number; offset: number },
  ): Promise<void> {
    const fd = new FormData()
    fd.append('file', blob, 'take.webm')
    fd.append('lineId', lineId)
    fd.append('duration', String(fields.duration))
    fd.append('offset', String(fields.offset))
    const res = await fetch(`/api/sessions/${sessionId}/takes`, {
      method: 'POST',
      body: fd,
      credentials: 'include',
    })
    if (!res.ok) throw ApiError.fromBody(res.status, await res.text())
  },
}
